import { useEffect } from 'react';

export interface ExternalLinkClick {
  href: string;
  anchor: HTMLAnchorElement;
  event: MouseEvent;
}

export function useExternalLinkClickListener({
  rootId,
  listener,
}: {
  rootId: string;
  listener: (info: ExternalLinkClick) => void;
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Find your widget root by ID
      const widgetRoot = document.getElementById(rootId);

      // Ignore if click is inside your widget
      if (widgetRoot && widgetRoot.contains(target)) {
        return;
      }

      // Find nearest <a href="...">
      const anchor = target.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Ignore internal hash, javascript links
      if (href.startsWith('#') || href.startsWith('javascript:')) {
        return;
      }

      listener({ href, anchor, event });
    };

    // capture phase so the SPA can't swallow the click
    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, {
        capture: true,
      } as any);
    };
  }, [rootId, listener]);
}
